import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Plus, Play, Download, X, Pencil, Trash2, ThumbsUp, MessageSquare,
  Clock, CheckCircle, AlertCircle, FileEdit, Send, Film, Video,
  Instagram, Facebook, Linkedin, Youtube, Sparkles, Mic, Loader2, PackageCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIDEO_TYPES = ['Ad Video', 'Social Media Video', 'Explainer Video', 'Brand Video', 'Testimonial Style'];
const PLATFORMS = ['Instagram', 'YouTube', 'LinkedIn', 'Facebook'];
const OBJECTIVES = ['Awareness', 'Leads', 'Engagement'];
const DURATIONS = ['15 sec', '30 sec', '60 sec'];
const VIDEO_STYLES = ['AI Avatar', 'Cinematic', 'Text-based', 'Slideshow'];
const ALL_STATUSES = ['draft', 'submitted', 'in_production', 'pending_approval', 'approved', 'delivered', 'changes_requested'];

const platformIcon = (p) => {
  if (p === 'Instagram') return Instagram;
  if (p === 'Facebook') return Facebook;
  if (p === 'LinkedIn') return Linkedin;
  if (p === 'YouTube') return Youtube;
  return Video;
};
const platformColor = (p) => {
  if (p === 'Instagram') return 'from-pink-500 to-purple-500';
  if (p === 'Facebook') return 'from-blue-500 to-blue-600';
  if (p === 'LinkedIn') return 'from-sky-600 to-blue-700';
  if (p === 'YouTube') return 'from-red-500 to-red-600';
  return 'from-violet-500 to-indigo-600';
};

const statusConfig = {
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600', icon: FileEdit },
  submitted: { label: 'Submitted', cls: 'bg-indigo-100 text-indigo-700', icon: Send },
  in_production: { label: 'In Production', cls: 'bg-violet-100 text-violet-700', icon: Loader2 },
  pending_approval: { label: 'Pending Approval', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  delivered: { label: 'Delivered', cls: 'bg-blue-100 text-blue-700', icon: PackageCheck },
  changes_requested: { label: 'Changes Requested', cls: 'bg-red-100 text-red-700', icon: AlertCircle },
};

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const useVideos = (firmId) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API}/videos/${firmId}`, { headers: authHeaders() });
      setVideos(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchVideos(); /* eslint-disable-next-line */ }, [firmId]);
  return { videos, loading, refetch: fetchVideos };
};

// ────────────────────────────────────────────────────────────────
// Feedback Modal
// ────────────────────────────────────────────────────────────────
function FeedbackModal({ onClose, onSubmit }) {
  const [text, setText] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="video-feedback-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Suggest Changes</h3>
        <p className="text-sm text-slate-500 mb-4">Share the edits you'd like us to make before approval.</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
          className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm resize-none mb-4"
          placeholder="Describe the changes..." data-testid="video-feedback-textarea" />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium">Cancel</button>
          <button onClick={() => { if (text.trim()) onSubmit(text); }} data-testid="video-feedback-submit-btn"
            className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl text-sm font-medium hover:shadow-lg">
            Submit Feedback
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Request Form Modal
// ────────────────────────────────────────────────────────────────
function RequestFormModal({ video, firmId, onClose, onSaved }) {
  const [form, setForm] = useState(video || {
    title: '', video_type: 'Ad Video', platform: 'Instagram', objective: 'Awareness',
    duration: '30 sec', script: '', notes: '', reference_links: '', status: 'draft'
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { alert('Video title required'); return; }
    setSaving(true);
    try {
      if (video?.id) {
        await axios.put(`${API}/videos/${video.id}`, form, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/videos/${firmId}`, form, { headers: authHeaders() });
      }
      onSaved();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const field = 'w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="video-request-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">{video?.id ? 'Edit Video Request' : 'New Video Request'}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Video Title</label>
            <input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} className={field} placeholder="e.g. Personal Injury Ad Video" data-testid="video-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Video Type</label>
              <select value={form.video_type} onChange={(e) => setForm(f => ({ ...f, video_type: e.target.value }))} className={field} data-testid="video-type-select">
                {VIDEO_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Platform</label>
              <select value={form.platform} onChange={(e) => setForm(f => ({ ...f, platform: e.target.value }))} className={field} data-testid="video-platform-select">
                {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Objective</label>
              <select value={form.objective} onChange={(e) => setForm(f => ({ ...f, objective: e.target.value }))} className={field} data-testid="video-objective-select">
                {OBJECTIVES.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Duration</label>
              <select value={form.duration} onChange={(e) => setForm(f => ({ ...f, duration: e.target.value }))} className={field} data-testid="video-duration-select">
                {DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Script (optional)</label>
            <textarea rows={3} value={form.script} onChange={(e) => setForm(f => ({ ...f, script: e.target.value }))} className={field + ' resize-none'} placeholder="Draft script..." data-testid="video-script-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes / Brief</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className={field + ' resize-none'} placeholder="Tone, audience, guardrails..." data-testid="video-notes-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reference Links</label>
            <input value={form.reference_links} onChange={(e) => setForm(f => ({ ...f, reference_links: e.target.value }))} className={field} placeholder="https://..., https://..." data-testid="video-refs-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className={field} data-testid="video-status-select">
              {ALL_STATUSES.map(s => <option key={s} value={s}>{statusConfig[s]?.label || s}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-medium transition-all hover:shadow-lg hover:shadow-violet-500/30 disabled:opacity-50" data-testid="video-save-btn">
              {saving ? 'Saving...' : (video?.id ? 'Update Request' : 'Create Request')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Production Form Modal (edit production-side fields)
// ────────────────────────────────────────────────────────────────
function ProductionFormModal({ video, onClose, onSaved }) {
  const [form, setForm] = useState({
    final_script: video.final_script || '',
    video_style: video.video_style || 'Cinematic',
    voiceover_type: video.voiceover_type || '',
    production_notes: video.production_notes || '',
    status: video.status,
    video_url: video.video_url || '',
    thumbnail_url: video.thumbnail_url || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/videos/${video.id}`, form, { headers: authHeaders() });
      onSaved();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const field = 'w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="video-production-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">Production · {video.title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Final Script</label>
            <textarea rows={4} value={form.final_script} onChange={(e) => setForm(f => ({ ...f, final_script: e.target.value }))} className={field + ' resize-none'} placeholder="Locked script for production..." data-testid="production-script-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Video Style</label>
              <select value={form.video_style} onChange={(e) => setForm(f => ({ ...f, video_style: e.target.value }))} className={field} data-testid="production-style-select">
                {VIDEO_STYLES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Voiceover</label>
              <input value={form.voiceover_type} onChange={(e) => setForm(f => ({ ...f, voiceover_type: e.target.value }))} className={field} placeholder="e.g. Warm female" data-testid="production-voiceover-input" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Production Notes</label>
            <textarea rows={2} value={form.production_notes} onChange={(e) => setForm(f => ({ ...f, production_notes: e.target.value }))} className={field + ' resize-none'} data-testid="production-notes-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Video URL</label>
              <input value={form.video_url} onChange={(e) => setForm(f => ({ ...f, video_url: e.target.value }))} className={field} placeholder="https://..." data-testid="production-video-url-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Thumbnail URL</label>
              <input value={form.thumbnail_url} onChange={(e) => setForm(f => ({ ...f, thumbnail_url: e.target.value }))} className={field} placeholder="https://..." data-testid="production-thumb-input" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className={field} data-testid="production-status-select">
              {ALL_STATUSES.map(s => <option key={s} value={s}>{statusConfig[s]?.label || s}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl text-sm font-medium">Cancel</button>
            <button onClick={save} disabled={saving} className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-medium transition-all hover:shadow-lg hover:shadow-violet-500/30 disabled:opacity-50" data-testid="production-save-btn">
              {saving ? 'Saving...' : 'Save Production'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Shared card pieces
// ────────────────────────────────────────────────────────────────
function VideoThumb({ video, size = 'md' }) {
  const Icon = platformIcon(video.platform);
  const h = size === 'lg' ? 'h-44' : 'h-32';
  return (
    <div className={`${h} bg-gradient-to-br from-slate-900 via-violet-900/60 to-slate-800 flex items-center justify-center relative overflow-hidden`}>
      {video.thumbnail_url && (
        <img src={video.thumbnail_url} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
      )}
      {!video.thumbnail_url && (
        <>
          <Film className="w-12 h-12 text-white/20 absolute" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(139,92,246,0.3),transparent)]" />
        </>
      )}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-xl">
          <Play className="w-5 h-5 text-violet-600 fill-violet-600 ml-0.5" />
        </div>
      </div>
      <div className={`absolute top-3 left-3 w-8 h-8 rounded-lg bg-gradient-to-br ${platformColor(video.platform)} flex items-center justify-center shadow-md`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      {video.duration && (
        <div className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/70 backdrop-blur-sm text-white text-xs rounded-md font-medium">
          {video.duration}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status, testid }) {
  const s = statusConfig[status] || statusConfig.draft;
  const SIcon = s.icon;
  const spin = status === 'in_production' ? 'animate-spin' : '';
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`} data-testid={testid}>
      <SIcon className={`w-3 h-3 ${spin}`} />{s.label}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────
// REQUESTS TAB
// ────────────────────────────────────────────────────────────────
function RequestsTab({ firmId, user, videos, loading, refetch, creditsCtx }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const isClient = user.role === 'client';
  const canCreate = creditsCtx ? creditsCtx.canCreate : true;
  const remaining = creditsCtx?.remaining ?? null;

  const seed = async () => {
    try { await axios.post(`${API}/videos/seed/${firmId}`, {}, { headers: authHeaders() }); refetch(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this video request?')) return;
    try { await axios.delete(`${API}/videos/${id}`, { headers: authHeaders() }); refetch(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };
  const notifyApproval = async (id) => {
    try { await axios.put(`${API}/videos/${id}`, { status: 'pending_approval' }, { headers: authHeaders() }); refetch(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading video requests...</div>;

  // Credits-aware "New Video Request" handler - for clients, fully gated by
  // remaining credits. For staff/admin, always enabled (legacy behaviour).
  const handleNewRequest = () => {
    if (isClient && !canCreate) {
      if (creditsCtx?.onBuyMore) creditsCtx.onBuyMore();
      return;
    }
    setEditing(null);
    setShowForm(true);
  };

  return (
    <div className="space-y-6" data-testid="video-requests">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Video Requests</h2>
        <div className="flex items-center gap-3">
          {!isClient && videos.length === 0 && (
            <button onClick={seed} className="px-4 py-2 text-sm text-violet-600 border border-violet-200 rounded-xl hover:bg-violet-50 font-medium transition-colors" data-testid="video-seed-btn">
              Load Sample Data
            </button>
          )}
          {/* Show the New Video Request button when:
              - Staff/admin always (legacy)
              - Clients only when the addon is unlocked */}
          {(!isClient || (creditsCtx && creditsCtx.unlocked)) && (
            <button
              onClick={handleNewRequest}
              disabled={isClient && remaining !== null && remaining <= 0}
              title={isClient && remaining !== null && remaining <= 0 ? 'No videos remaining. Purchase more to continue.' : ''}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-medium text-sm transition-all hover:shadow-lg hover:shadow-violet-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="new-video-request-btn"
            >
              <Plus className="w-4 h-4" />New Video Request
            </button>
          )}
        </div>
      </div>

      {videos.length === 0 ? (
        <div className="text-center py-16 bg-white/70 rounded-2xl border border-white/50">
          <Video className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No video requests yet</p>
          <p className="text-slate-400 text-sm mt-1">Create your first AI video request</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {videos.map((v) => (
            <div key={v.id} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={`video-card-${v.id}`}>
              <VideoThumb video={v} />
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-slate-900 line-clamp-1 flex-1">{v.title}</h3>
                  <StatusBadge status={v.status} testid={`video-status-${v.id}`} />
                </div>
                <div className="text-xs text-slate-500 mt-1">{v.video_type} · {v.objective}</div>
                {v.notes && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{v.notes}</p>}
                {v.feedback && (
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid={`video-feedback-${v.id}`}>
                    <span className="font-medium">Feedback:</span> {v.feedback}
                  </div>
                )}
                <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-slate-100">
                  {!isClient && v.status === 'draft' && (
                    <button onClick={() => notifyApproval(v.id)} className="px-2.5 py-1 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg" data-testid={`video-notify-${v.id}`}>
                      Notify for Approval
                    </button>
                  )}
                  {!isClient && (
                    <>
                      <button onClick={() => { setEditing(v); setShowForm(true); }} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" data-testid={`video-edit-${v.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(v.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={`video-delete-${v.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <RequestFormModal video={editing} firmId={firmId} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={() => { setShowForm(false); setEditing(null); refetch(); }} />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// PRODUCTION TAB
// ────────────────────────────────────────────────────────────────
function ProductionTab({ user, videos, loading, refetch }) {
  const [editing, setEditing] = useState(null);
  const isClient = user.role === 'client';
  const prodVideos = useMemo(() =>
    videos.filter(v => ['submitted', 'in_production', 'changes_requested'].includes(v.status)),
    [videos]
  );

  const markReady = async (id) => {
    try { await axios.put(`${API}/videos/${id}`, { status: 'pending_approval' }, { headers: authHeaders() }); refetch(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading production queue...</div>;

  return (
    <div className="space-y-6" data-testid="video-production">
      <div>
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Production Queue</h2>
        <p className="text-slate-500 text-sm mt-1">Internal production tracking for videos in progress.</p>
      </div>

      {prodVideos.length === 0 ? (
        <div className="text-center py-16 bg-white/70 rounded-2xl border border-white/50">
          <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Nothing in production</p>
          <p className="text-slate-400 text-sm mt-1">Submitted and in-production videos will appear here.</p>
        </div>
      ) : (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm overflow-hidden">
          <table className="w-full" data-testid="production-table">
            <thead className="bg-slate-50/80 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Title</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Type / Style</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Last Updated</th>
                <th className="px-6 py-4" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {prodVideos.map(v => (
                <tr key={v.id} className="hover:bg-violet-50/30 transition-colors" data-testid={`production-row-${v.id}`}>
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-900">{v.title}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{v.platform} · {v.duration}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="text-slate-700">{v.video_type}</div>
                    {v.video_style && <div className="text-xs text-slate-400 flex items-center gap-1"><Mic className="w-3 h-3" />{v.video_style}</div>}
                  </td>
                  <td className="px-6 py-4"><StatusBadge status={v.status} testid={`production-status-${v.id}`} /></td>
                  <td className="px-6 py-4 text-sm text-slate-500">{new Date(v.updated_at).toLocaleDateString()}</td>
                  <td className="px-6 py-4 text-right">
                    {!isClient && (
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => setEditing(v)} className="px-3 py-1.5 text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-lg" data-testid={`production-edit-${v.id}`}>
                          Edit Production
                        </button>
                        {v.status !== 'pending_approval' && (
                          <button onClick={() => markReady(v.id)} className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg" data-testid={`production-ready-${v.id}`}>
                            Notify for Approval
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <ProductionFormModal video={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// APPROVALS TAB
// ────────────────────────────────────────────────────────────────
function ApprovalsTab({ user, videos, loading, refetch }) {
  const [feedbackFor, setFeedbackFor] = useState(null);
  const isClient = user.role === 'client';
  const approvalVideos = useMemo(() =>
    videos.filter(v => ['pending_approval', 'approved', 'changes_requested'].includes(v.status)),
    [videos]
  );

  const updateStatus = async (id, status, feedback) => {
    try {
      const body = { status };
      if (feedback !== undefined) body.feedback = feedback;
      await axios.put(`${API}/videos/${id}`, body, { headers: authHeaders() });
      refetch();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };
  const markDelivered = async (id) => {
    try { await axios.put(`${API}/videos/${id}`, { status: 'delivered' }, { headers: authHeaders() }); refetch(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading approvals...</div>;

  return (
    <div className="space-y-6" data-testid="video-approvals">
      <div>
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Client Approvals</h2>
        <p className="text-slate-500 text-sm mt-1">Videos ready for client review before final delivery.</p>
      </div>

      {approvalVideos.length === 0 ? (
        <div className="text-center py-16 bg-white/70 rounded-2xl border border-white/50">
          <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">Nothing to approve right now</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {approvalVideos.map(v => (
            <div key={v.id} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={`approval-card-${v.id}`}>
              <VideoThumb video={v} size="lg" />
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-slate-900 flex-1 line-clamp-1">{v.title}</h3>
                  <StatusBadge status={v.status} testid={`approval-status-${v.id}`} />
                </div>
                <div className="text-xs text-slate-500 mt-1">{v.platform} · {v.duration}</div>
                {v.feedback && (
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid={`approval-feedback-${v.id}`}>
                    <span className="font-medium">Feedback:</span> {v.feedback}
                  </div>
                )}
                <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
                  {isClient && v.status === 'pending_approval' && (
                    <>
                      <button onClick={() => updateStatus(v.id, 'approved')} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-xl" data-testid={`approval-approve-${v.id}`}>
                        <ThumbsUp className="w-3.5 h-3.5" />Approve
                      </button>
                      <button onClick={() => setFeedbackFor(v.id)} className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-xl" data-testid={`approval-suggest-${v.id}`}>
                        <MessageSquare className="w-3.5 h-3.5" />Suggest Changes
                      </button>
                    </>
                  )}
                  {!isClient && v.status === 'approved' && (
                    <button onClick={() => markDelivered(v.id)} className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg" data-testid={`approval-deliver-${v.id}`}>
                      Mark as Delivered
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {feedbackFor && <FeedbackModal onClose={() => setFeedbackFor(null)} onSubmit={(text) => { updateStatus(feedbackFor, 'changes_requested', text); setFeedbackFor(null); }} />}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// DELIVERED TAB
// ────────────────────────────────────────────────────────────────
function DeliveredTab({ user, videos, loading, refetch }) {
  const isClient = user.role === 'client';
  const delivered = useMemo(() => videos.filter(v => v.status === 'delivered'), [videos]);

  const updateMeta = async (id, fields) => {
    try { await axios.put(`${API}/videos/${id}`, fields, { headers: authHeaders() }); refetch(); }
    catch (err) { alert(err.response?.data?.detail || 'Error'); }
  };

  if (loading) return <div className="text-center py-12 text-slate-500">Loading delivered videos...</div>;

  return (
    <div className="space-y-6" data-testid="video-delivered">
      <div>
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Delivered Videos</h2>
        <p className="text-slate-500 text-sm mt-1">Final approved videos ready for use.</p>
      </div>

      {delivered.length === 0 ? (
        <div className="text-center py-16 bg-white/70 rounded-2xl border border-white/50">
          <PackageCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No delivered videos yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {delivered.map(v => (
            <div key={v.id} className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/50 shadow-sm hover:shadow-md transition-shadow overflow-hidden" data-testid={`delivered-card-${v.id}`}>
              <VideoThumb video={v} size="lg" />
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-slate-900 flex-1 line-clamp-1">{v.title}</h3>
                  <StatusBadge status="delivered" testid={`delivered-status-${v.id}`} />
                </div>
                <div className="text-xs text-slate-500 mt-1">{v.platform} · {v.duration}</div>
                {v.delivery_date && <div className="text-xs text-slate-400 mt-1">Delivered {new Date(v.delivery_date).toLocaleDateString()}</div>}
                <div className="flex items-center gap-2 mt-3">
                  {v.video_url ? (
                    <>
                      <a href={v.video_url} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:shadow-lg rounded-lg" data-testid={`delivered-play-${v.id}`}>
                        <Play className="w-3.5 h-3.5" />Play
                      </a>
                      <a href={v.video_url} download className="px-3 py-2 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg" data-testid={`delivered-download-${v.id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    </>
                  ) : (
                    <div className="text-xs text-slate-400 italic">Video URL pending upload</div>
                  )}
                </div>
                {(v.used_in_campaign || v.published_on_platform) && (
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-1 text-xs text-slate-500">
                    {v.used_in_campaign && <div><span className="font-medium text-slate-600">Used in:</span> {v.used_in_campaign}</div>}
                    {v.published_on_platform && <div><span className="font-medium text-slate-600">Published on:</span> {v.published_on_platform}</div>}
                  </div>
                )}
                {!isClient && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                    <input
                      defaultValue={v.used_in_campaign || ''}
                      onBlur={(e) => e.target.value !== (v.used_in_campaign || '') && updateMeta(v.id, { used_in_campaign: e.target.value })}
                      className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-lg"
                      placeholder="Used in campaign..."
                      data-testid={`delivered-campaign-input-${v.id}`}
                    />
                    <input
                      defaultValue={v.published_on_platform || ''}
                      onBlur={(e) => e.target.value !== (v.published_on_platform || '') && updateMeta(v.id, { published_on_platform: e.target.value })}
                      className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-lg"
                      placeholder="Published on..."
                      data-testid={`delivered-published-input-${v.id}`}
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Main export
// ────────────────────────────────────────────────────────────────
import AIVideoPaywallModal from './AIVideoPaywallModal';
import AIVideoPortfolio from './AIVideoPortfolio';
import AddonEntitlements from './AddonEntitlements';
import { Lock, Wallet, Film as FilmIcon, ShoppingCart } from 'lucide-react';

export default function AIVideo({ user, selectedClient }) {
  const [activeTab, setActiveTab] = useState('requests');
  const firmId = selectedClient?.firm_id || selectedClient?.firmId || user.firm?.id || user.firm_id;
  const isClient = user.role === 'client';
  const { videos, loading, refetch } = useVideos(firmId);

  const [billing, setBilling] = useState(null); // null = loading, object = resolved
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallMode, setPaywallMode] = useState('unlock'); // 'unlock' | 'topup'

  const refreshBilling = async () => {
    if (!firmId) return null;
    try {
      const res = await axios.get(`${API}/me/billing`, { headers: authHeaders() });
      setBilling(res.data || {});
      return res.data;
    } catch (_e) { setBilling({}); return null; }
  };

  useEffect(() => {
    if (!firmId) return;
    refreshBilling();
  }, [firmId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open paywall ONLY when client lands here without an unlocked addon
  useEffect(() => {
    if (!billing || !isClient) return;
    const unlocked = !!(billing.ai_video_unlocked || billing.manual_override_enabled);
    if (!unlocked) {
      setPaywallMode('unlock');
      setPaywallOpen(true);
    }
  }, [billing, isClient]);

  // Post-Stripe-checkout polling - same pattern as the other add-ons
  useEffect(() => {
    if (!firmId || !isClient) return undefined;
    if (billing?.ai_video_unlocked) return undefined;
    let stamp;
    try { stamp = localStorage.getItem('ai_video_paying_at'); } catch (_e) { stamp = null; }
    if (!stamp) return undefined;
    const ageMs = Date.now() - new Date(stamp).getTime();
    if (Number.isNaN(ageMs) || ageMs > 1000 * 60 * 30) return undefined;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      const data = await refreshBilling();
      if (data?.ai_video_unlocked) {
        try { localStorage.removeItem('ai_video_paying_at'); } catch (_e) { /* ignore */ }
        return;
      }
      if (attempts >= 20) return;
      setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
  }, [firmId, isClient, billing?.ai_video_unlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also poll briefly after every paywall close (covers top-up purchases too)
  useEffect(() => {
    if (paywallOpen) return undefined;
    let stamp;
    try { stamp = localStorage.getItem('ai_video_paying_at'); } catch (_e) { stamp = null; }
    if (!stamp) return undefined;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      const before = billing?.videos_purchased || 0;
      const data = await refreshBilling();
      if ((data?.videos_purchased || 0) > before) {
        try { localStorage.removeItem('ai_video_paying_at'); } catch (_e) { /* ignore */ }
        return;
      }
      if (attempts >= 20) return;
      setTimeout(tick, 3000);
    };
    tick();
    return () => { cancelled = true; };
  }, [paywallOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { id: 'requests', label: 'Requests' },
    { id: 'production', label: 'Production' },
    { id: 'approvals', label: 'Approvals' },
    { id: 'delivered', label: 'Delivered' },
  ];

  if (!firmId) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Select a client to manage videos.</p>
      </div>
    );
  }

  // Wait for billing snapshot before rendering - avoids a flash of locked UI.
  if (isClient && billing === null) {
    return <div className="text-center py-16 text-slate-400" data-testid="ai-video-loading">Loading AI Video…</div>;
  }

  const unlocked = !isClient || !!(billing?.ai_video_unlocked || billing?.manual_override_enabled);
  const purchased = Number(billing?.videos_purchased || 0);
  const used = Number(billing?.videos_used || 0);
  const remaining = Math.max(0, purchased - used);
  const isLocked = !unlocked;

  const openTopup = () => { setPaywallMode('topup'); setPaywallOpen(true); };
  const openUnlock = () => { setPaywallMode('unlock'); setPaywallOpen(true); };

  // Allow children to programmatically request a credit-aware "Create" gate.
  // RequestsTab already has its own Add button; we expose remaining + a guard
  // via `creditsCtx` so the RequestsTab can disable the button when needed.
  const creditsCtx = {
    isClient,
    unlocked,
    remaining,
    canCreate: unlocked && remaining > 0,
    onBuyMore: openTopup,
    refreshBilling,
  };

  return (
    <div className="space-y-6 animate-fadeInUp relative" data-testid="ai-video-module">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>AI Video</h1>
        <p className="text-slate-500 mt-1">Request, produce, approve, and deliver AI-assisted videos.</p>
      </div>

      <AddonEntitlements firmId={firmId} addonKey="ai_video" />

      {/* Credits / usage card - only meaningful when the addon is unlocked */}
      {unlocked && (
        <div
          className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-indigo-600 to-violet-700 p-6 shadow-xl shadow-violet-500/25"
          data-testid="ai-video-credits-card"
        >
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="grid grid-cols-3 gap-6 flex-1">
              {[
                { label: 'Videos Purchased', value: purchased, icon: ShoppingCart, testid: 'ai-video-stat-purchased' },
                { label: 'Videos Used', value: used, icon: FilmIcon, testid: 'ai-video-stat-used' },
                { label: 'Videos Remaining', value: remaining, icon: Wallet, testid: 'ai-video-stat-remaining' },
              ].map((s) => (
                <div key={s.label} className="text-white" data-testid={s.testid}>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-violet-100/90">
                    <s.icon className="w-3.5 h-3.5" />
                    {s.label}
                  </div>
                  <div className="text-3xl font-bold mt-1" style={{ fontFamily: 'Outfit' }}>{s.value}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
              <button
                onClick={() => {
                  if (remaining <= 0) { openTopup(); return; }
                  // Jump to the Requests tab so the existing Add Video Request flow opens
                  setActiveTab('requests');
                }}
                disabled={remaining <= 0}
                className="px-5 py-2.5 rounded-xl bg-white text-violet-700 font-semibold text-sm hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                data-testid="ai-video-create-new-btn"
                title={remaining <= 0 ? 'No videos remaining. Purchase more to continue.' : ''}
              >
                Create New Video
              </button>
              <button
                onClick={openTopup}
                className="px-5 py-2.5 rounded-xl bg-white/15 text-white font-semibold text-sm border border-white/20 hover:bg-white/25 transition-all"
                data-testid="ai-video-buy-more-btn"
              >
                Buy More Videos
              </button>
            </div>
          </div>
          {remaining <= 0 && (
            <div
              className="relative z-10 mt-4 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-sm text-white font-medium"
              data-testid="ai-video-no-credits-banner"
            >
              No videos remaining. Purchase more to continue.
            </div>
          )}
          <div className="absolute -right-10 -bottom-10 w-48 h-48 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        </div>
      )}

      {/* Locked layout: tabs + content dimmed; clicks intercepted via overlay */}
      <div className={isLocked ? 'pointer-events-none opacity-50 select-none' : ''} aria-disabled={isLocked} data-testid="ai-video-locked-overlay-target">
        <div className="flex items-center gap-1 bg-slate-100/80 rounded-xl p-1 overflow-x-auto" data-testid="video-tabs">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => !isLocked && setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`video-tab-${tab.id}`}>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'requests' && <RequestsTab firmId={firmId} user={user} videos={videos} loading={loading} refetch={() => { refetch(); refreshBilling(); }} creditsCtx={creditsCtx} />}
        {activeTab === 'production' && <ProductionTab user={user} videos={videos} loading={loading} refetch={refetch} />}
        {activeTab === 'approvals' && <ApprovalsTab user={user} videos={videos} loading={loading} refetch={refetch} />}
        {activeTab === 'delivered' && <DeliveredTab user={user} videos={videos} loading={loading} refetch={refetch} />}
      </div>

      {/* Portfolio showcase - below the working area so the request flow is seen first */}
      <div className="pt-2" id="ai-video-portfolio-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-[11px] font-semibold tracking-[0.15em] text-slate-400 uppercase">Portfolio</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>
        <AIVideoPortfolio />
      </div>

      {/* Locked CTA banner - clients can re-open the paywall after dismiss */}
      {isLocked && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 px-5 py-3 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 shadow-2xl shadow-violet-900/40 flex items-center gap-3" data-testid="ai-video-locked-cta">
          <Lock className="w-4 h-4 text-white" />
          <span className="text-white text-sm font-semibold">Unlock AI Video to start producing</span>
          <button onClick={openUnlock} className="px-3 py-1.5 rounded-lg bg-white text-violet-700 text-xs font-bold hover:bg-violet-50 transition" data-testid="ai-video-reopen-paywall">
            Choose plan
          </button>
        </div>
      )}

      <AIVideoPaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        mode={paywallMode}
        onSeeSamples={() => {
          setPaywallOpen(false);
          setTimeout(() => {
            document.getElementById('ai-video-portfolio-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 150);
        }}
      />
    </div>
  );
}
